// Доменная логика интервалов доставки RU-курьера (Москва/СПб): окно слотов
// от cutoff (beforeTime), праздничная подмена времени, формат эммитов и
// момент следующего пересчёта для heartbeat.
//
// Модуль чистый: момент времени приходит снаружи (now), таймеров и DOM нет —
// оркестрация живёт в @/steps/step4/composables/useDeliveryIntervals.
import { DateTime } from 'luxon'

export const EXPRESS_SERVICE_ID = 'I_COURIER_1DAY'

// Майские праздники сверяются с хвостом текста опции («EEEE, d MMMM»,
// последние 6 символов « N MMMM») — обновляется вручную раз в год.
const HOLIDAY_DAY_LABELS = [
  ' 2 мая',
  ' 3 мая',
  ' 4 мая',
  ' 5 мая',
  ' 6 мая',
  ' 7 мая',
  ' 8 мая',
]
export const HOLIDAY_TIME = '09:00-18:00'

export const isExpressService = (deliveryServiceId) =>
  deliveryServiceId === EXPRESS_SERVICE_ID

export const selectIntervalSource = (deliveryTimes, { isExpress, mkad }) =>
  deliveryTimes[isExpress ? 'express' : mkad ? 'mkad' : 'outside']

export const isHolidayOption = (option) =>
  HOLIDAY_DAY_LABELS.some((label) => option?.text?.endsWith(label))

// Окно дней, в котором ищем слоты: до cutoff обычная доставка стартует с
// завтра, после — с послезавтра; экспресс — сегодня/завтра
export function getSlotWindow({ now, beforeTimeToday, isExpress }) {
  const pastCutoff = now.hour >= beforeTimeToday
  const startDay = isExpress ? (pastCutoff ? 1 : 0) : pastCutoff ? 2 : 1
  return { startDay, endDay: isExpress ? startDay + 1 : startDay + 7 }
}

// Опции дней и расписание слотов: дни с skip !== 1 и даты из restricted
// не попадают в выдачу; у текущего дня срезаются прошедшие интервалы.
export function buildDeliverySlots({
  now,
  intervals,
  isExpress,
  restricted = [],
}) {
  const nowDay = now.weekday - 1
  const { startDay, endDay } = getSlotWindow({
    now,
    beforeTimeToday: intervals[nowDay]?.beforeTime,
    isExpress,
  })

  const options = []
  const schedule = {}
  for (let dayOffset = startDay; dayOffset < endDay; dayOffset++) {
    const id = (nowDay + dayOffset) % 7
    if (intervals[id]?.skip !== 1) continue

    const date = now.plus({ days: dayOffset })
    let times = intervals[id].times
    if (dayOffset === 0) {
      const currentTime = now.toFormat('HH:mm')
      times = times.filter((time) => time.split('-')[0] > currentTime)
    }

    if (times.length > 0 && !restricted.includes(date.toFormat('dd.MM.yyyy'))) {
      options.push({
        id,
        text: `${date.toFormat('EEEE')}, ${date.toFormat('d MMMM')}`,
      })
      schedule[id] = { date: date.toFormat('yyyyMMdd'), times }
    }
  }
  return { options, schedule }
}

export const buildTimeComment = (day, time) => `${day.text}, ${time}`

export const buildDeliveryTimePayload = (schedule, day, time) => ({
  DELIVERY_DATE: schedule[day?.id]?.date,
  DELIVERY_FROM: time.split('-')[0],
  DELIVERY_TO: time.split('-')[1],
})

// Ближайший момент, когда состав слотов может измениться: cutoff сегодня или
// следующий известный cutoff, старт последнего express-слота, якорь-полночь.
export function computeNextRecalcAt({ now, intervals, isExpress }) {
  const todayIdx = (now.weekday - 1) % 7
  const btToday = Number(intervals[todayIdx]?.beforeTime)
  const candidates = []

  if (Number.isFinite(btToday) && now.hour < btToday) {
    candidates.push(now.startOf('day').plus({ hours: Math.ceil(btToday) }))
  } else {
    for (let i = 1; i <= 7; i++) {
      const day = now.startOf('day').plus({ days: i })
      const bt = Number(intervals[(day.weekday - 1) % 7]?.beforeTime)
      if (Number.isFinite(bt)) {
        candidates.push(day.plus({ hours: Math.ceil(bt) }))
        break
      }
    }
  }

  if (isExpress && Number.isFinite(btToday) && now.hour < btToday) {
    const currentTime = now.toFormat('HH:mm')
    const starts = (intervals[todayIdx]?.times ?? [])
      .map((time) => time.split('-')[0])
      .filter((start) => start > currentTime)
      .sort() // times не гарантированно отсортированы — берём минимум
    if (starts[0]) {
      const [h, m] = starts[0].split(':').map(Number)
      candidates.push(now.startOf('day').plus({ hours: h, minutes: m }))
    }
  }

  candidates.push(now.startOf('day').plus({ days: 1 }))
  return DateTime.min(...candidates)
}
